import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, of, switchMap, withLatestFrom } from 'rxjs';

import { ExportCsvConfig, ExportDataService } from 'src/app/services/file/export-data.service';
import { ListingPageModel } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { TableData } from '@portal/shared/types/api-response.types';
import { DevicesPayload } from 'src/app/pages/equipment/devices/components/devices.models';
import { roundedDecimalSymbols } from '@portal/shared/utils/math.utils';

import { selectUnitOfMeasure } from '../../../consumption-analytics/store/consumption-analytics.reducer';
import { IndividualConsumptionTableAPIService } from '../individual-consumption-table-api.service';
import { ConsumptionAnalyticsFieldName, IndividualConsumptionsItem } from '../individual-consumption-table.types';
import { IndividualConsumptionTableActions } from './individual-consumption-table.actions';
import { individualConsumptionTableFeature } from './individual-consumption-table.reducer';

export const fetchTableDataTriggered$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(IndividualConsumptionTableActions.payloadUpdated),
      map(() => IndividualConsumptionTableActions.getGridData())
    ),
  { functional: true }
);

export const fetchTableData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), api = inject(IndividualConsumptionTableAPIService)) =>
    actions$.pipe(
      ofType(IndividualConsumptionTableActions.getGridData),
      withLatestFrom(store.select(individualConsumptionTableFeature.selectPayload)),
      filter(([, payload]) => payload !== null),
      exhaustMap(([, payload]) =>
        api.getListingData(payload!).pipe(
          map(({ items, totalCount }) =>
            IndividualConsumptionTableActions.getGridDataSuccess({
              data: items,
              total: totalCount
            })
          ),
          catchError((error: unknown) =>
            of(IndividualConsumptionTableActions.getGridDataFailure({ error: String(error) }))
          )
        )
      )
    ),
  { functional: true }
);

export const export$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    api = inject(IndividualConsumptionTableAPIService),
    exportService = inject(ExportDataService)
  ) =>
    actions$.pipe(
      ofType(IndividualConsumptionTableActions.exportCsv),
      withLatestFrom(
        store.select(individualConsumptionTableFeature.selectTableData),
        store.select(individualConsumptionTableFeature.selectPayload),
        store.select(selectUnitOfMeasure)
      ),
      filter(([, , payload]) => payload !== null),
      switchMap(([action, { total }, payload, unitOfMeasureDef]) => {
        const { visibility, order } = action;
        const { model: unitOfMeasureModel, multiplier } = unitOfMeasureDef;
        const selectedDirectionFlowName: string | null =
          payload?.filters?.find(filter => filter.filterType === ConsumptionAnalyticsFieldName.FlowDirection)
            ?.filteredValues[0] ?? null;
        const listingPageModel = {
          dataSource: {
            getAll: () =>
              api.getListingData(payload!).pipe(
                map(({ items, totalCount }) => ({
                  data: items.map(item => ({
                    ...item,
                    totalValue: item.totalValue ? roundedDecimalSymbols(item.totalValue * multiplier) : item.totalValue,
                    averageValue: item.averageValue
                      ? roundedDecimalSymbols(item.averageValue * multiplier)
                      : item.averageValue,
                    minimumValue: item.minimumValue
                      ? roundedDecimalSymbols(item.minimumValue * multiplier)
                      : item.minimumValue,
                    maximumValue: item.maximumValue
                      ? roundedDecimalSymbols(item.maximumValue * multiplier)
                      : item.maximumValue
                  })),
                  total: totalCount
                }))
              ),
            total: total
          },
          exportCsv: {
            fileName: `Export-${selectedDirectionFlowName ?? ''}TotalIndividualConsumption`,
            mapData: ({ data }: TableData<IndividualConsumptionsItem>): ExportCsvConfig[] => {
              return data.map(item => {
                const unitOfMeasure = unitOfMeasureModel.selectedItem.text;
                return {
                  deviceSerialNumber: { title: 'SerialNumber', value: item.deviceSerialNumber },
                  accountNumber: {
                    title: 'AccountNumber',
                    value:
                      item.accountNumber && item.accountSubNumber
                        ? `${item.accountNumber} / ${item.accountSubNumber}`
                        : ''
                  },
                  commDeviceId: { title: 'CommDeviceID', value: item.commDeviceId },
                  port: { title: 'Port', value: item.port },
                  deviceModel: { title: 'Model', value: item.deviceModel },
                  totalValue: { title: `Total(${unitOfMeasure})`, value: item.totalValue },
                  averageValue: { title: `Average(${unitOfMeasure})`, value: item.averageValue },
                  maximumValue: { title: `Max(${unitOfMeasure})`, value: item.maximumValue },
                  minimumValue: { title: `Min(${unitOfMeasure})`, value: item.minimumValue },
                  serviceLocationAddress: { title: 'ServiceLocationAddress', value: item.serviceLocationAddress }
                };
              });
            }
          }
        } as Pick<ListingPageModel, 'dataSource' | 'exportCsv'>;

        return exportService.exportTableToCsv$(payload as DevicesPayload, listingPageModel, total, visibility, order);
      })
    ),
  { functional: true, dispatch: false }
);
